import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  HostBinding,
  ElementRef,
  HostListener,
  Renderer2,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { VehicleHistoryDrawerData } from '../../models/vehicle-history-drawer.model';

@Component({
  selector: 'app-vehicle-history-drawer',
  templateUrl: './vehicle-history-drawer.component.html',
  styleUrls: ['./vehicle-history-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VehicleHistoryDrawerComponent implements AfterViewInit, OnDestroy {
  @Input() isHidden = true;
  @Input() dataHistoryDrawer: VehicleHistoryDrawerData = {
    variant: 'custom',
    imei: '',
  };
  @Output() hiddenHistoryDrawer = new EventEmitter<void>();
  @Output() destroyedHistoryDrawer = new EventEmitter<void>();
  @Output() mapResize = new EventEmitter<void>(); // Evento para centrar el mapa

  mapResizeEvent = false;

  // Variable para controlar el ancho del panel
  private _panelWidth = 920; // Ancho por defecto

  // Variables para el redimensionamiento
  private isResizing = false;
  private startX = 0;
  private startWidth = 0;
  private minWidth = 560; // Ancho mínimo
  private maxWidth = 1200; // Ancho máximo

  // Event listeners del documento
  private documentMouseMoveListener: (event: MouseEvent) => void;
  private documentMouseUpListener: (event: MouseEvent) => void;

  constructor(private elementRef: ElementRef, private renderer: Renderer2) {
    // Crear los listeners del documento
    this.documentMouseMoveListener = this.onDocumentMouseMove.bind(this);
    this.documentMouseUpListener = this.onDocumentMouseUp.bind(this);
  }

  ngAfterViewInit() {
    this.initializeResizeBar();
  }

  ngOnDestroy() {
    this.removeDocumentListeners();
  }

  private initializeResizeBar() {
    this.startWidth = this._panelWidth;
    this.updateCSSVariable(this._panelWidth);
  }

  // HostBinding para la variable CSS
  @HostBinding('style.--map-aside-width') get mapAsideWidth() {
    return `${this._panelWidth}px`;
  }

  // Getter público para acceder al ancho
  get panelWidth(): number {
    return this._panelWidth;
  }

  @HostListener('mousedown', ['$event'])
  onMouseDown(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (target.classList.contains('resize-handle')) {
      this.isResizing = true;
      this.startX = event.clientX;
      this.startWidth = this._panelWidth;

      event.preventDefault();
      this.renderer.addClass(document.body, 'resizing');

      // Agregar listeners del documento
      this.addDocumentListeners();
    }
  }

  private addDocumentListeners() {
    // Usar arrow functions para mantener el contexto de 'this'
    document.addEventListener('mousemove', (event: MouseEvent) => {
      this.onDocumentMouseMove(event);
    });

    document.addEventListener('mouseup', (event: MouseEvent) => {
      this.onDocumentMouseUp(event);
    });
  }

  private removeDocumentListeners() {
    // Remover todos los listeners de mousemove y mouseup
    document.removeEventListener('mousemove', this.documentMouseMoveListener);
    document.removeEventListener('mouseup', this.documentMouseUpListener);
  }

  private onDocumentMouseMove(event: MouseEvent) {
    if (!this.isResizing) return;

    const deltaX = event.clientX - this.startX;
    // Para deslizamiento hacia la izquierda (agrandar), restamos deltaX
    // Para deslizamiento hacia la derecha (achicar), sumamos deltaX
    const newWidth = Math.max(
      this.minWidth,
      Math.min(this.maxWidth, this.startWidth - deltaX)
    );

    this.updateWidth(newWidth);
  }

  private onDocumentMouseUp(event: MouseEvent) {
    if (this.isResizing) {
      this.isResizing = false;
      this.renderer.removeClass(document.body, 'resizing');

      // Remover listeners del documento
      this.removeDocumentListeners();

      // Emitir evento para que el mapa se recentre
      this.mapResize.emit();

      this.mapResizeEvent = true;

      setTimeout(() => {
        this.mapResizeEvent = false;
      }, 100);
    }
  }

  private updateWidth(width: number) {
    // Actualizar la variable del componente
    this._panelWidth = width;

    // Actualizar la variable CSS
    this.updateCSSVariable(width);
  }

  private updateCSSVariable(width: number) {
    // Actualizar la variable CSS del host
    this.renderer.setStyle(
      this.elementRef.nativeElement,
      '--map-aside-width',
      `${width}px`
    );
  }

  hiddenHistoryMap() {
    this.hiddenHistoryDrawer.emit();
  }

  destroyedHistoryMap() {
    this.destroyedHistoryDrawer.emit();
  }
}
